import { useGraphqlClient } from '../graphql';
import { useCreatePersonalPropertyItemMutation } from '../generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCreateFileAttachment } from '../FileAttachment';
import {
  CreatePersonalPropertyItemServiceInput,
  UpdatePersonalPropertyItemValueServiceInput,
  UpdatePersonalPropertyItemServiceInput,
} from './types';
import { useCreateDataPoint } from '../DataPoint';
import { ttDetailsKeys } from '../TtDetails';
import { scorecardKeys } from '../Scorecard';
import { personalPropertyItemKeys } from './queryKeys';
import { useUpdatePersonalPropertyItem } from './mutations';

export const useUpdatePersonalPropertyItemValueService = () => {
  const { createDataPoint } = useCreateDataPoint();
  const queryClient = useQueryClient();
  const updatePersonalPropertyItemValueService = useMutation({
    mutationKey: ['updatePersonalPropertyItemValueService'],
    mutationFn: async (input: UpdatePersonalPropertyItemValueServiceInput) => {
      await createDataPoint.mutateAsync({
        dataPoint: {
          dateTime: new Date().toISOString(),
          data: {
            monetaryAmountValue: {
              value: {
                currencyCode: 'USD',
                value: input.valueInCents,
              },
            },
          },
          groupID: input.valueDataPointGroupID,
        },
        tenantID: input.householdID,
      });
    },
    onSuccess: (_, { householdID, personalPropertyItemID }) => {
      queryClient.invalidateQueries(
        personalPropertyItemKeys.personalPropertyItem({ householdID, id: personalPropertyItemID })
      );
    },
  });

  return { updatePersonalPropertyItemValueService };
};

export function useCreatePersonalPropertyItemService() {
  const { createFileAttachment } = useCreateFileAttachment();
  const createPersonalPropertyItem = useCreatePersonalPropertyItemMutation(useGraphqlClient());
  const { updatePersonalPropertyItemValueService } = useUpdatePersonalPropertyItemValueService();
  const queryClient = useQueryClient();

  const createPersonalPropertyItemService = useMutation({
    mutationFn: async ({
      files,
      createPersonalPropertyItemMutationInput,
      valueInCents,
    }: CreatePersonalPropertyItemServiceInput) => {
      const {
        createPersonalPropertyItem: { personalPropertyItem },
      } = await createPersonalPropertyItem.mutateAsync({ input: createPersonalPropertyItemMutationInput });

      if (valueInCents) {
        updatePersonalPropertyItemValueService.mutate({
          valueInCents,
          householdID: personalPropertyItem.householdID,
          personalPropertyItemID: personalPropertyItem.id,
          valueDataPointGroupID: personalPropertyItem.value.id,
        });
      }

      files?.forEach((file) => {
        createFileAttachment.mutate({
          tenantID: personalPropertyItem.householdID,
          fileAttachment: { itemID: personalPropertyItem.id, fileID: file.id },
        });
      });
      return personalPropertyItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(ttDetailsKeys.base);
      queryClient.invalidateQueries(scorecardKeys.base);
    },
  });

  return { createPersonalPropertyItemService };
}

/**
 * @returns a function that:
 * 1. Updates a personal property item
 * 2. Creates a data point for the personal property item value data point group
 * 3. Creates file attachments for any pending files
 */
export const useUpdatePersonalPropertyItemService = () => {
  const { updatePersonalPropertyItemValueService } = useUpdatePersonalPropertyItemValueService();
  const { updatePersonalPropertyItem } = useUpdatePersonalPropertyItem();
  const { createFileAttachment } = useCreateFileAttachment();
  const queryClient = useQueryClient();

  const updatePersonalPropertyItemService = useMutation({
    mutationKey: ['updatePersonalPropertyItemService'],
    mutationFn: async (input: UpdatePersonalPropertyItemServiceInput) => {
      const { personalPropertyItem } = await updatePersonalPropertyItem.mutateAsync(
        input.updatePersonalPropertyItemInput,
        input.updatePersonalPropertyItemMutationOptions
      );

      await updatePersonalPropertyItemValueService.mutateAsync({
        householdID: personalPropertyItem.householdID,
        valueDataPointGroupID: personalPropertyItem.value.id,
        valueInCents: input.valueInCents,
        personalPropertyItemID: personalPropertyItem.id,
      });

      await Promise.all(
        (input.files || []).map((pendingFile) =>
          createFileAttachment.mutateAsync({
            tenantID: personalPropertyItem.householdID,
            fileAttachment: { fileID: pendingFile.id, itemID: personalPropertyItem.id },
          })
        )
      );
    },
    onSuccess: (_, { updatePersonalPropertyItemInput: { householdID, id } }) => {
      queryClient.invalidateQueries(personalPropertyItemKeys.personalPropertyItem({ householdID, id }));
    },
  });

  return { updatePersonalPropertyItemService };
};
